import { FAKE, REAL, registerDependency } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'


export const EXTERNAL_VALIDATION_SKIPPED = 'EXTERNAL_VALIDATION_SKIPPED' as const

type PhoneNumberValidationResult = {
  isValidNumber: boolean
  regionCodeForNumber: string
  formatE164: string
}

export type GoogleLibphonenumber = {
  initialize: (countryCodes: string[]) => void
  validatePhoneNumber: (userInput: string) => PhoneNumberValidationResult | typeof EXTERNAL_VALIDATION_SKIPPED
}

export const GOOGLE_LIBPHONENUMBER = Symbol('GOOGLE_LIBPHONENUMBER')


@registerDependency(FAKE, GOOGLE_LIBPHONENUMBER)
export class FakeGoogleLibphonenumber implements GoogleLibphonenumber {
  public userInput: string[] = []
  public validationResult: PhoneNumberValidationResult = {
    isValidNumber: true,
    regionCodeForNumber: 'unknown',
    formatE164: 'invalid',
  }

  private countryCodes: string[] = []
  private initialized = false

  initialize = (countryCodes: string[]): void => {
    this.countryCodes = countryCodes
    this.initialized = true
  }

  validatePhoneNumber = (userInput: string): PhoneNumberValidationResult | typeof EXTERNAL_VALIDATION_SKIPPED => {
    if (!this.initialized) {
      throw new Error('GoogleLibphonenumber is not initialized correctly')
    }
    if (this.countryCodes.length === 0) {
      return EXTERNAL_VALIDATION_SKIPPED
    }
    this.userInput.push(userInput)
    return this.validationResult
  }
}


@registerDependency(REAL, GOOGLE_LIBPHONENUMBER)
export class RealGoogleLibphonenumber implements GoogleLibphonenumber {
  private countryCodes: string[] = []
  private static scriptLoaded = false
  private static scriptLoadingPromise: Promise<void> | null = null

  initialize = (countryCodes: string[]): void => {
    if (countryCodes.length === 0) { return }

    this.countryCodes = countryCodes

    if (RealGoogleLibphonenumber.scriptLoaded || window.libphonenumber) {
      RealGoogleLibphonenumber.scriptLoaded = true
      return
    }

    if (!RealGoogleLibphonenumber.scriptLoadingPromise) {
      RealGoogleLibphonenumber.scriptLoadingPromise = new Promise<void>((resolve) => {
        const existing = document.querySelector('script[data-cse-libphonenumber]') as HTMLScriptElement | null
        if (existing) {
          existing.addEventListener('load', () => {
            RealGoogleLibphonenumber.scriptLoaded = true
            resolve()
          }, { once: true })
          return
        }
        const element: HTMLScriptElement = document.createElement('script')
        element.defer = true
        element.src = `${process.env['MINIO_CDN_BASE_URL'] ?? 'https://s3-cdn.cloudsuite.com'}/${process.env['MINIO_CDN_BUCKET_PREFIX'] ?? ''}google-libphonenumber/3.2.36/libphonenumber.js`
        element.setAttribute('data-cse-libphonenumber', 'true')
        element.addEventListener('load', () => {
          RealGoogleLibphonenumber.scriptLoaded = true
          resolve()
        }, { once: true })
        document.body.appendChild(element)
      })
    }
  }

  validatePhoneNumber = (userInput: string): PhoneNumberValidationResult | typeof EXTERNAL_VALIDATION_SKIPPED => {
    if (window.libphonenumber === undefined) {
      return EXTERNAL_VALIDATION_SKIPPED
    }

    const util = window.libphonenumber.PhoneNumberUtil.getInstance()
    const formatE164 = window.libphonenumber.PhoneNumberFormat.E164

    for (const countryCode of this.countryCodes) {
      try {
        const phoneNumber = util.parse(userInput, countryCode)
        const isValidNumber = util.isValidNumber(phoneNumber)

        if (!isValidNumber) { continue }

        return {
          isValidNumber: isValidNumber,
          regionCodeForNumber: util.getRegionCodeForNumber(phoneNumber),
          formatE164: util.format(phoneNumber, formatE164),
        }
      }
      catch { continue }
    }

    return {
      isValidNumber: false,
      regionCodeForNumber: '',
      formatE164: '',
    }
  }
}


type PhoneNumber = any
type PhoneNumberUtil = {
  parse: (input: string, countryCode: string) => PhoneNumber
  isValidNumber: (phoneNumber: PhoneNumber) => boolean
  getRegionCodeForNumber: (phoneNumber: PhoneNumber) => string
  format: (phoneNumber: PhoneNumber, format: E164) => string
}

type E164 = 0
type PhoneNumberFormat = {
  E164: E164,
}

type Libphonenumber = {
  PhoneNumberUtil: {
    getInstance: () => PhoneNumberUtil
  }
  PhoneNumberFormat: PhoneNumberFormat
}

declare global {
  interface Window {
    libphonenumber?: Libphonenumber
  }
}
